import React, { useState } from "react";
import { useLocation } from "@/context/LocationContext";
import { Navigation, Search, Loader2, Home, Briefcase, MapPin } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { v: "Home", icon: Home },
  { v: "Work", icon: Briefcase },
  { v: "Other", icon: MapPin },
];

export default function AddressForm({ initial, onSubmit, onCancel, saving }) {
  const { detectCurrent, detecting, searchAddress, reverseGeocode } = useLocation();
  const [a, setA] = useState({
    address_type: "Home", house_no: "", building: "", street: "", locality: "",
    landmark: "", city: "", state: "", pincode: "", lat: null, lng: null, ...(initial || {}),
  });
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const set = (k) => (e) => setA({ ...a, [k]: e.target.value });

  const useGPS = async () => {
    try {
      const loc = await detectCurrent();
      setA((p) => ({ ...p, city: loc.city || p.city, state: loc.state || p.state,
        pincode: loc.pincode || p.pincode, locality: p.locality || loc.address, lat: loc.lat, lng: loc.lng }));
      toast.success("Location detected — please complete the remaining fields");
    } catch {
      toast.error("Location access denied. Please enter your address manually.");
    }
  };

  const doSearch = async (v) => {
    setQ(v);
    if (v.length < 3) { setResults([]); return; }
    try { setResults(await searchAddress(v)); } catch { /* ignore */ }
  };

  const pick = async (r) => {
    setResults([]); setQ(r.address);
    let g = { city: r.city, state: r.state, pincode: r.pincode };
    if (!g.city || !g.pincode) { try { g = await reverseGeocode(r.lat, r.lng); } catch { /* ignore */ } }
    setA((p) => ({ ...p, city: g.city || p.city, state: g.state || p.state,
      pincode: g.pincode || p.pincode, locality: p.locality || r.address, lat: r.lat, lng: r.lng }));
  };

  const submit = () => {
    if (!a.house_no && !a.building && !a.street) return toast.error("Enter your house/flat no. and street");
    if (!a.city || !a.pincode) return toast.error("City and PIN code are required");
    onSubmit({ ...a, label: a.address_type });
  };

  const fieldCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand";
  const field = (k, ph, req) => (
    <input value={a[k] || ""} onChange={set(k)} placeholder={ph + (req ? " *" : "")} data-testid={`addr-${k}`} className={fieldCls} />
  );

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 p-3" data-testid="address-form">
      <div className="flex items-center gap-2">
        <button type="button" onClick={useGPS} disabled={detecting} data-testid="addr-use-current-location-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-brand-soft px-3 py-2 text-sm font-bold text-brand">
          {detecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />} Use current location
        </button>
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => doSearch(e.target.value)} placeholder="Search area / locality" data-testid="addr-search-input"
            className="w-full rounded-lg border border-slate-200 py-2 pl-8 pr-2 text-sm outline-none focus:border-brand" />
        </div>
      </div>
      {results.length > 0 && (
        <div className="max-h-40 space-y-1 overflow-auto rounded-lg border border-slate-100 p-1">
          {results.map((r, i) => (
            <button key={i} type="button" onClick={() => pick(r)} data-testid={`addr-search-result-${i}`}
              className="flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-slate-100">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" /><span>{r.full}</span>
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        {TYPES.map((t) => (
          <button key={t.v} type="button" onClick={() => setA({ ...a, address_type: t.v })} data-testid={`addr-type-${t.v.toLowerCase()}`}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${a.address_type === t.v ? "border-brand bg-brand-soft text-brand" : "border-slate-200 text-slate-600"}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.v}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {field("house_no", "House / Flat No.", true)}
        {field("building", "Building name")}
        {field("street", "Street / Road")}
        {field("locality", "Area / Locality")}
        {field("landmark", "Landmark (optional)")}
        {field("city", "City", true)}
        {field("state", "State")}
        {field("pincode", "PIN Code", true)}
      </div>
      <div className="flex gap-2">
        <button onClick={submit} disabled={saving} data-testid="addr-save-btn" className="rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white disabled:opacity-60">Save address</button>
        {onCancel && <button onClick={onCancel} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancel</button>}
      </div>
    </div>
  );
}
